import React, { createContext, useContext, useId } from 'react'

/**
 * Lightweight context so FieldLabel / FieldDescription can pick up the
 * generated id and state from an ancestor <Field> without needing Radix's
 * Slot/Context machinery.
 */
const FieldContext = createContext(null)

export function useField () {
  const ctx = useContext(FieldContext)
  // Allow FieldLabel/FieldDescription to be used outside a <Field> too —
  // just fall back to no shared id/state instead of throwing.
  return ctx ?? { id: undefined, descriptionId: undefined, invalid: false, disabled: false }
}

function cx (...classes) {
  return classes.filter(Boolean).join(' ')
}

/**
 * FieldGroup — vertical stack of Fields (or FieldSets) with consistent
 * spacing. Purely a layout primitive.
 */
export function FieldGroup ({ className, children, ...props }) {
  return (
    <div data-slot='field-group' className={cx('field-group', className)} {...props}>
      {children}
    </div>
  )
}

/**
 * Field — wraps one form control (label + input + description/error).
 * orientation:
 *   'vertical'   -> label stacked above control (default)
 *   'horizontal' -> label and control side by side
 *   'responsive' -> vertical on small screens, horizontal from `sm` up
 */
export function Field ({
  orientation = 'vertical',
  invalid = false,
  disabled = false,
  className,
  children,
  ...props
}) {
  const generatedId = useId()
  const id = props.id ?? generatedId
  const descriptionId = `${id}-description`

  return (
    <FieldContext.Provider value={{ id, descriptionId, invalid, disabled }}>
      <div
        data-slot='field'
        data-orientation={orientation}
        data-invalid={invalid || undefined}
        data-disabled={disabled || undefined}
        className={cx('field', `field--${orientation}`, className)}
        {...props}
      >
        {children}
      </div>
    </FieldContext.Provider>
  )
}

/**
 * FieldLabel — a plain <label>. If used inside a <Field>, it automatically
 * targets that field's generated id unless you pass htmlFor explicitly.
 * Remember to also put `id={field's id}` on your actual <input>/<select> —
 * this component doesn't clone children the way Radix's Slot would, so the
 * association has to be made explicitly on the control.
 */
export function FieldLabel ({ htmlFor, className, children, ...props }) {
  const { id, invalid, disabled } = useField()

  return (
    <label
      data-slot='field-label'
      htmlFor={htmlFor ?? id}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      className={cx('field-label', className)}
      {...props}
    >
      {children}
    </label>
  )
}

/**
 * FieldDescription — muted helper text under a field. When used inside a
 * <Field>, exposes an id you can wire to your control's aria-describedby.
 */
export function FieldDescription ({ className, children, ...props }) {
  const { descriptionId } = useField()

  return (
    <p
      data-slot='field-description'
      id={descriptionId}
      className={cx('field-description', className)}
      {...props}
    >
      {children}
    </p>
  )
}

export default Field
